import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, RefreshCw, AlertTriangle } from "lucide-react";

const field = "w-full mt-1.5 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-base sm:text-sm text-slate-900 focus:border-sky-200 focus:outline-none";
const FIELDS = [
  ["target_customer", "WHO — your potential customer", 2],
  ["problem", "PROBLEM — what you'd solve for them", 2],
  ["outcome", "OUTCOME — what changes for them", 2],
  ["offer", "OFFER — what you'd actually sell", 3],
  ["delivery_model", "FORMAT — how you'd deliver it", 2],
  ["first_validation_action", "FIRST TEST — the smallest sensible market test", 3],
  ["validation_channel", "CHANNEL — where you'd test it", 1],
];

export const StepBlueprint = ({ state, patch, onNext }) => {
  const bp = state.blueprint;
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(FIELDS.map(([k]) => [k, bp?.[k] || ""])),
    price_amount: bp?.price_hypothesis?.amount || "",
    price_basis: bp?.price_hypothesis?.basis || "",
  }));
  const [saving, setSaving] = useState(false);
  const [redrafting, setRedrafting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const symbol = bp?.target_currency === "USD" ? "$" : "₹";

  if (!bp) return <p className="text-sm text-slate-500" data-testid="accelerator-no-blueprint">Choose your primary direction first.</p>;

  const save = async (advance = false) => {
    setSaving(true);
    try {
      const payload = {
        ...Object.fromEntries(FIELDS.map(([k]) => [k, form[k] || null])),
        price_amount: form.price_amount === "" ? null : Number(form.price_amount),
        price_basis: form.price_basis || null,
      };
      const { data } = await api.put("/accelerator/blueprint", payload);
      patch({ blueprint: data.blueprint, progress: data.progress });
      toast.success("Blueprint saved");
      if (advance) onNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your blueprint");
    } finally {
      setSaving(false);
    }
  };

  const redraft = async () => {
    setRedrafting(true);
    try {
      const { data } = await api.post("/accelerator/blueprint?refresh=true");
      patch({ blueprint: data.blueprint, progress: data.progress });
      setForm((f) => ({
        ...f,
        ...Object.fromEntries(FIELDS.map(([k]) => [k, data.blueprint[k] || ""])),
        price_amount: data.blueprint.price_hypothesis?.amount || "",
        price_basis: data.blueprint.price_hypothesis?.basis || "",
      }));
      toast.success("Fresh draft ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redraft");
    } finally {
      setRedrafting(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="accelerator-step-blueprint-panel">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <h2 className="text-base md:text-lg font-bold text-slate-900">Step 5 — Your 2nd Income Blueprint</h2>
        <p className="text-sm text-slate-500 mt-1.5">A first draft built from your confirmed capability and chosen direction. Edit anything — it's your plan, and every price here is a hypothesis until the market answers.</p>
        {bp.review_needed && (
          <p className="mt-3 inline-flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="blueprint-review-note">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Your profile or direction changed — review your blueprint, then redraft if you want a fresh version.
          </p>
        )}
      </div>

      {(!!bp.existing_value?.length || !!bp.why_you?.length) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {[["Your existing value", bp.existing_value], ["Why you", bp.why_you], ["Proof you already have", bp.existing_proof], ["What you still need to prove", bp.missing_proof], ["Still unknown", bp.unknowns]].map(([label, items]) => items?.length ? (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-4" data-testid={`blueprint-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc list-inside">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          ) : null)}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-4" data-testid="accelerator-offer-builder">
        <h3 className="font-bold text-slate-900">Offer builder</h3>
        {FIELDS.map(([key, label, rows]) => (
          <label key={key} className="block text-xs text-slate-500">{label}
            <textarea rows={rows} value={form[key]} onChange={(e) => set(key, e.target.value)} className={field} data-testid={`blueprint-input-${key}`} />
          </label>
        ))}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-xs text-slate-500">PRICE HYPOTHESIS ({symbol}) — not validated yet
            <input type="number" min={0} value={form.price_amount} onChange={(e) => set("price_amount", e.target.value)} className={field} data-testid="blueprint-input-price" />
          </label>
          <label className="block text-xs text-slate-500">Price basis
            <input value={form.price_basis} onChange={(e) => set("price_basis", e.target.value)} className={field} data-testid="blueprint-input-price-basis" />
          </label>
        </div>
      </div>

      <p className="text-xs text-slate-500" data-testid="blueprint-target-moved-note">
        Your validation target and the ways to reach it now live on the next step — Your 2nd Income Validator.
      </p>

      {!!bp.constraint_notes?.length && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="blueprint-constraint-notes">
          <p className="text-xs font-semibold text-amber-700">Notes on your constraints</p>
          <ul className="mt-1.5 space-y-1 text-xs text-amber-700 list-disc list-inside">{bp.constraint_notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <Button onClick={() => save(false)} disabled={saving} className="h-11 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="blueprint-save-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
        </Button>
        <Button onClick={redraft} disabled={redrafting} variant="outline" className="h-11 border-slate-300 text-slate-700" data-testid="blueprint-redraft-button">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${redrafting ? "animate-spin" : ""}`} /> Redraft
        </Button>
        <Button onClick={() => save(true)} disabled={saving} className="h-11 col-span-2 sm:col-span-1 bg-sky-600 hover:bg-sky-500 text-white" data-testid="blueprint-continue-button">Save & go to validation</Button>
      </div>
    </section>
  );
};

export default StepBlueprint;
